import {
  cdr,
  formatDuration,
  formatPhone,
  formatWhen,
  orchestrator,
  useAuth,
  useCan,
  type CallHistoryResponse,
  type CallRecord,
  type ClientRecord,
  type TranscriptResponse,
  type TranscriptSegment,
} from "@savvy/runtime";
import { Caps } from "@savvy/shared";
import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@savvy/ui";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Call history — the gate's "call history + playable recording".
 *
 * Filters live in the URL, so a supervisor can send a colleague a link to
 * exactly what they are looking at. Paging is the server's keyset cursor;
 * "Load more" appends, it never re-fetches the pages already shown.
 */

const PRESETS: Record<string, () => { from: string; to: string }> = {
  today: () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return { from: d.toISOString(), to: new Date().toISOString() };
  },
  "7d": () => ({
    from: new Date(Date.now() - 7 * 86_400_000).toISOString(),
    to: new Date().toISOString(),
  }),
  "30d": () => ({
    from: new Date(Date.now() - 30 * 86_400_000).toISOString(),
    to: new Date().toISOString(),
  }),
};

// Radix select items cannot carry an empty value; "any" is the URL-absent
// sentinel and is translated at the edge so the URL params stay unchanged.
const ANY = "any";

const errText = "rounded-md bg-danger-bg px-2.5 py-1.5 text-sm text-danger";

export function HistoryView() {
  const api = useAuth((s) => s.api);
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState<CallRecord[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const preset = params.get("range") ?? "today";
  const direction = params.get("direction") ?? "";
  const disposition = params.get("disposition") ?? "";
  const number = params.get("number") ?? "";
  const clientId = params.get("client") ?? "";

  // Client filter options. Listing needs clients.manage; a supervisor without
  // it just doesn't get the dropdown — the column still renders.
  const [clients, setClients] = useState<ClientRecord[] | null>(null);
  useEffect(() => {
    orchestrator(api)
      .listClients()
      .then(setClients)
      .catch(() => setClients(null));
  }, [api]);

  const load = useCallback(
    async (append: boolean, after?: string) => {
      setLoading(true);
      setError(null);
      try {
        const range = (PRESETS[preset] ?? PRESETS["today"]!)();
        const res: CallHistoryResponse = await cdr(api).history({
          from: range.from,
          to: range.to,
          direction: direction || undefined,
          disposition: disposition ? [disposition] : undefined,
          client_id: clientId || undefined,
          number: number || undefined,
          cursor: after,
          limit: 50,
        });
        setItems((prev) => (append ? [...prev, ...res.items] : res.items));
        setCursor(res.next_cursor);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    [api, preset, direction, disposition, clientId, number],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  const setFilter = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  return (
    <section className="mb-8">
      <h2 className="mb-2 text-base font-semibold">Call history</h2>
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <Select value={preset} onValueChange={(v) => setFilter("range", v)}>
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={direction || ANY}
          onValueChange={(v) => setFilter("direction", v === ANY ? "" : v)}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any direction</SelectItem>
            <SelectItem value="inbound">Inbound</SelectItem>
            <SelectItem value="outbound">Outbound</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={disposition || ANY}
          onValueChange={(v) => setFilter("disposition", v === ANY ? "" : v)}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any outcome</SelectItem>
            <SelectItem value="answered">Answered</SelectItem>
            <SelectItem value="abandoned">Abandoned</SelectItem>
            <SelectItem value="no_agent">No agent</SelectItem>
            <SelectItem value="busy">Busy</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        {clients && clients.length > 0 && (
          <Select value={clientId || ANY} onValueChange={(v) => setFilter("client", v === ANY ? "" : v)}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any client</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Input
          className="w-56"
          placeholder="Number (last digits ok)"
          defaultValue={number}
          onKeyDown={(e) => {
            if (e.key === "Enter") setFilter("number", (e.target as HTMLInputElement).value);
          }}
        />
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>When</TableHead>
            <TableHead>From → To</TableHead>
            <TableHead>Client</TableHead>
            <TableHead>Call type</TableHead>
            <TableHead>Outcome</TableHead>
            <TableHead>Queue / Agent</TableHead>
            <TableHead>Wait</TableHead>
            <TableHead>Talk</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={9} className="text-muted-foreground">
                no calls in this range
              </TableCell>
            </TableRow>
          )}
          {items.map((c) => (
            <HistoryRow
              key={c.call_uuid}
              call={c}
              open={open === c.call_uuid}
              onToggle={() => setOpen(open === c.call_uuid ? null : c.call_uuid)}
            />
          ))}
        </TableBody>
      </Table>

      {error && <p className={errText}>{error}</p>}
      {cursor && (
        <Button className="mt-2" variant="outline" disabled={loading} onClick={() => void load(true, cursor)}>
          {loading ? "Loading…" : "Load more"}
        </Button>
      )}
    </section>
  );
}

function HistoryRow({
  call,
  open,
  onToggle,
}: {
  call: CallRecord;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <TableRow onClick={onToggle} className="cursor-pointer">
        <TableCell className={cn(open && "border-b-0")}>{formatWhen(call.started_at)}</TableCell>
        <TableCell className={cn(open && "border-b-0")}>
          {call.caller_name ? `${call.caller_name} · ` : ""}
          {formatPhone(call.caller_number)} → {formatPhone(call.destination ?? "")}
        </TableCell>
        <TableCell className={cn(open && "border-b-0")}>
          {/* Who the call was FOR at the time. An inbound call with no client
              means the DID was unmapped when it rang — an admin's todo. */}
          {call.client_name ||
            (call.direction === "inbound" ? (
              <Badge variant="warning">unmapped</Badge>
            ) : (
              ""
            ))}
        </TableCell>
        <TableCell className={cn(open && "border-b-0")}>
          {/* Why they rang, as the agent classified it. Blank is normal: they
              may not have picked one, or the client has no call types. */}
          {call.call_type_name ?? ""}
        </TableCell>
        <TableCell
          className={cn(
            open && "border-b-0",
            call.disposition === "answered" && "text-success",
            (call.disposition === "abandoned" || call.disposition === "no_agent") && "text-danger",
          )}
        >
          {call.disposition.replace("_", " ")}
        </TableCell>
        <TableCell className={cn(open && "border-b-0")}>
          {call.queue ?? ""}
          {call.agent ? ` · ${call.agent}` : ""}
        </TableCell>
        <TableCell className={cn("font-mono tabular-nums", open && "border-b-0")}>
          {formatDuration(call.wait_sec)}
        </TableCell>
        <TableCell className={cn("font-mono tabular-nums", open && "border-b-0")}>
          {formatDuration(call.talk_sec)}
        </TableCell>
        <TableCell className={cn(open && "border-b-0")}>{call.has_recording ? "●" : ""}</TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={9} className="bg-card text-card-foreground">
            <CallDetail call={call} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function CallDetail({ call }: { call: CallRecord }) {
  const api = useAuth((s) => s.api);
  const canPlay = useCan(Caps.TelephonyRecordingsPlay);
  const canExport = useCan(Caps.TelephonyRecordingsExport);
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The playback URL carries its own short-lived signed grant, minted here by
  // a capability-checked request — an <audio> element cannot send a bearer
  // token, and it re-requests with a Range header on every seek.
  useEffect(() => {
    if (!call.has_recording || !canPlay) return;
    let stop = false;
    cdr(api)
      .recordingUrl(call.call_uuid)
      .then((r) => {
        if (!stop) setSrc(r.url);
      })
      .catch((e: unknown) => {
        if (!stop) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      stop = true;
    };
  }, [api, call, canPlay]);

  const download = async () => {
    try {
      const r = await cdr(api).recordingUrl(call.call_uuid, true);
      window.open(r.url, "_blank");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="p-1">
      <dl className="my-1.5 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
        <dt className="text-muted-foreground">Started</dt>
        <dd className="m-0">{new Date(call.started_at).toLocaleString()}</dd>
        {/* answered_at and bridged_at stay separate on purpose: for a queued
            call, "answered" is hold music; "bridged" is a human. */}
        <dt className="text-muted-foreground">Agent picked up</dt>
        <dd className="m-0">
          {call.bridged_at ? new Date(call.bridged_at).toLocaleString() : "never"}
        </dd>
        <dt className="text-muted-foreground">Ended</dt>
        <dd className="m-0">
          {new Date(call.ended_at).toLocaleString()}
          {call.hangup_cause ? ` (${call.hangup_cause})` : ""}
        </dd>
      </dl>
      {call.has_recording && canPlay && (
        <div className="mt-2 flex items-center gap-3">
          {src ? (
            <audio controls preload="metadata" src={src} />
          ) : error ? null : (
            <span className="text-muted-foreground">loading recording…</span>
          )}
          {canExport && (
            <Button variant="outline" size="sm" onClick={() => void download()}>
              Download
            </Button>
          )}
        </div>
      )}
      {!call.has_recording && <p className="text-muted-foreground">This call was not recorded.</p>}
      {error && <p className={errText}>{error}</p>}
      {call.has_recording && <TranscriptSection uuid={call.call_uuid} />}
    </div>
  );
}

function TranscriptSection({ uuid }: { uuid: string }) {
  const api = useAuth((s) => s.api);
  const canView = useCan(Caps.TelephonyTranscriptsView);
  const [t, setT] = useState<TranscriptResponse | null>(null);
  const [absent, setAbsent] = useState(false);

  useEffect(() => {
    if (!canView) return;
    let stop = false;
    cdr(api)
      .transcript(uuid)
      .then((res) => {
        if (!stop) setT(res);
      })
      .catch((e: unknown) => {
        // 404 = no transcript row; anything else is worth showing via absent
        // too — the transcript is auxiliary to the call detail.
        if (!stop) setAbsent(true);
        void e;
      });
    return () => {
      stop = true;
    };
  }, [api, uuid, canView]);

  if (!canView || absent) return null;
  if (!t) return <p className="text-muted-foreground">loading transcript…</p>;

  const segments = (t.segments ?? []) as TranscriptSegment[];
  return (
    <div className="mt-3">
      <h3 className="mb-1.5 text-sm font-semibold">
        Transcript
        {t.status === "pending" && <span className="font-normal text-muted-foreground"> — being transcribed…</span>}
        {t.status === "failed" && <span className="font-normal text-danger"> — failed: {t.error}</span>}
        {t.status === "done" && t.language && <span className="font-normal text-muted-foreground"> · {t.language}</span>}
      </h3>
      {t.status === "done" && segments.length > 0 && (
        <ol className="m-0 max-h-80 list-none overflow-y-auto p-0 text-sm">
          {segments.map((s, i) => (
            <li key={i} className="py-0.5">
              <span className="mr-1.5 font-mono text-xs text-muted-foreground tabular-nums">
                {formatDuration(Math.floor(s.start))}
              </span>
              {s.speaker && <strong> {s.speaker}:</strong>} {s.text}
            </li>
          ))}
        </ol>
      )}
      {t.status === "done" && segments.length === 0 && (
        <p className="text-muted-foreground">No speech was recognized in this recording.</p>
      )}
    </div>
  );
}
